import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Plus, Pencil } from "lucide-react"
import { toast } from "sonner"
import { api, type ApiEnvelope, type TeamMember } from "@/lib/api"
import { getErrorMessage, getFieldErrors } from "@/lib/get-error-message"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { FieldError } from "@/components/ui/field-error"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog"
import { ConfirmDelete } from "@/components/admin/confirm-delete"
import { ImageUpload } from "@/components/admin/image-upload"

export const Route = createFileRoute("/admin/_authed/team")({
  component: TeamPage,
})

type FormState = {
  name: string
  designation: string
  bio: string
  order: number
  is_active: boolean
  linkedin: string
  twitter: string
  github: string
}
const empty: FormState = {
  name: "",
  designation: "",
  bio: "",
  order: 0,
  is_active: true,
  linkedin: "",
  twitter: "",
  github: "",
}

function TeamPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery<TeamMember[]>({
    queryKey: ["admin", "team"],
    queryFn: async () => (await api.get<ApiEnvelope<TeamMember[]>>("/admin/team")).data.data,
  })

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<TeamMember | null>(null)
  const [form, setForm] = useState<FormState>(empty)
  const [photo, setPhoto] = useState<File | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "team"] })
    queryClient.invalidateQueries({ queryKey: ["team"] })
  }

  const save = useMutation({
    mutationFn: async () => {
      const fd = new FormData()
      fd.append("name", form.name)
      fd.append("designation", form.designation)
      fd.append("bio", form.bio)
      fd.append("order", String(form.order))
      fd.append("is_active", form.is_active ? "1" : "0")
      fd.append("social_links[linkedin]", form.linkedin)
      fd.append("social_links[twitter]", form.twitter)
      fd.append("social_links[github]", form.github)
      if (photo) fd.append("photo", photo)
      if (editing) {
        fd.append("_method", "PUT")
        await api.post(`/admin/team/${editing.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      } else {
        await api.post("/admin/team", fd, { headers: { "Content-Type": "multipart/form-data" } })
      }
    },
    onSuccess: () => {
      invalidate()
      setOpen(false)
      setFieldErrors({})
      toast.success(editing ? "Team member updated." : "Team member created.")
    },
    onError: (error) => {
      setFieldErrors(getFieldErrors(error))
      toast.error(getErrorMessage(error))
    },
  })

  const remove = useMutation({
    mutationFn: async (id: number) => api.delete(`/admin/team/${id}`),
    onSuccess: () => {
      invalidate()
      toast.success("Team member deleted.")
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openCreate = () => {
    setEditing(null)
    setForm(empty)
    setPhoto(null)
    setFieldErrors({})
    setOpen(true)
  }
  const openEdit = (item: TeamMember) => {
    setEditing(item)
    setForm({
      name: item.name,
      designation: item.designation,
      bio: item.bio ?? "",
      order: item.order,
      is_active: item.is_active,
      linkedin: item.social_links?.linkedin ?? "",
      twitter: item.social_links?.twitter ?? "",
      github: item.social_links?.github ?? "",
    })
    setPhoto(null)
    setFieldErrors({})
    setOpen(true)
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Team</h1>
          <p className="mt-1 text-sm text-muted-foreground">People shown on the about and team pages.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-full">
              <Plus className="h-4 w-4" /> New member
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit team member" : "New team member"}</DialogTitle>
            </DialogHeader>
            <form
              className="space-y-4"
              noValidate
              onSubmit={(e) => {
                e.preventDefault()
                save.mutate()
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    aria-invalid={!!fieldErrors.name}
                  />
                  <FieldError message={fieldErrors.name} />
                </div>
                <div>
                  <Label htmlFor="designation">Designation</Label>
                  <Input
                    id="designation"
                    value={form.designation}
                    onChange={(e) => setForm({ ...form, designation: e.target.value })}
                    aria-invalid={!!fieldErrors.designation}
                  />
                  <FieldError message={fieldErrors.designation} />
                </div>
              </div>
              <div>
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  aria-invalid={!!fieldErrors.bio}
                />
                <FieldError message={fieldErrors.bio} />
              </div>
              <ImageUpload label="Photo" currentUrl={editing?.photo_url} onChange={setPhoto} rounded="rounded-full" />
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <Label htmlFor="linkedin">LinkedIn</Label>
                  <Input id="linkedin" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="twitter">Twitter</Label>
                  <Input id="twitter" value={form.twitter} onChange={(e) => setForm({ ...form, twitter: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="github">GitHub</Label>
                  <Input id="github" value={form.github} onChange={(e) => setForm({ ...form, github: e.target.value })} />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 sm:items-end">
                <div>
                  <Label htmlFor="order">Order</Label>
                  <Input
                    id="order"
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                  />
                </div>
                <div className="flex items-center gap-3 pb-2">
                  <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} id="is_active" />
                  <Label htmlFor="is_active">Active</Label>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8">
        {isLoading ? (
          <div className="h-40 animate-pulse rounded-2xl bg-secondary" />
        ) : isError ? (
          <p className="text-sm text-destructive">Couldn't load team members.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {(data ?? []).map((m) => (
              <div key={m.id} className="rounded-2xl border border-border bg-card p-6 text-center">
                {m.photo_url ? (
                  <img src={m.photo_url} alt={m.name} className="mx-auto h-16 w-16 rounded-full object-cover object-top" />
                ) : (
                  <div className="mx-auto h-16 w-16 rounded-full bg-secondary" />
                )}
                <p className="mt-3 font-semibold">{m.name}</p>
                <p className="text-xs text-muted-foreground">{m.designation}</p>
                <Badge className="mt-2" variant={m.is_active ? "default" : "secondary"}>
                  {m.is_active ? "Active" : "Hidden"}
                </Badge>
                <div className="mt-4 flex justify-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(m)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <ConfirmDelete itemLabel={m.name} onConfirm={() => remove.mutate(m.id)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
